import { useCopilotAction } from '@copilotkit/react-core';

import { Gateway } from 'types/gateway';

import { useAPI } from 'hooks/api/useAPI';

export function useListGatewaysTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listGateways',
    description:
      'List gateways (edge devices) in the system. Use this to find gateway IDs for filtering analytics or file searches. Can filter by status, name, or model.',
    parameters: [
      {
        name: 'ids',
        type: 'string',
        description: 'JSON array of gateway IDs to fetch',
        required: false,
      },
      {
        name: 'statuses',
        type: 'string',
        description:
          'JSON array of statuses to filter by: ["online", "offline", "never_seen"]',
        required: false,
      },
      {
        name: 'names',
        type: 'string',
        description: 'JSON array of gateway names to search for',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      ids?: string | string[];
      statuses?: string | string[];
      names?: string | string[];
      limit?: number;
    }) => {
      const parseArrayParam = (param: string | string[] | undefined): string[] | undefined => {
        if (!param) return undefined;
        if (Array.isArray(param)) return param;
        try {
          return JSON.parse(param);
        } catch {
          // If it's not valid JSON, treat it as a single value
          return [param];
        }
      };

      try {
        const ids = parseArrayParam(args.ids);
        const statuses = parseArrayParam(args.statuses);
        const names = parseArrayParam(args.names);

        const { data } = await client.get(`/apps/${applicationID}/gateways`, {
          params: {
            pagination: 'offset',
            page: 1,
            limit: args.limit || 50,
            gateway_ids: ids,
            statuses,
            gateway_names: names,
          },
        });

        const gateways = data?.data || [];

        if (gateways.length === 0) {
          return {
            message: 'No gateways found matching your criteria.',
            gateways: [],
          };
        }

        return {
          message: `Found ${gateways.length} gateway(s)`,
          gateways: gateways.map((gateway: Gateway) => gateway),
        };
      } catch (error) {
        return {
          message: 'Failed to load gateways',
          error: error instanceof Error ? error.message : 'Unknown error',
          gateways: [],
        };
      }
    },
  });
}
